import clsx from 'clsx';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRegisterFirstStore } from '@lib/store';
import { Button, PhoneInput, TextInput } from '@shared/widgets';

interface IForm {
  cardNumber: string;
  mobileNumber: { code: string; number: string };
}
const DEFAULT_VALUES: IForm = { cardNumber: '', mobileNumber: { code: '', number: '' } };

interface StepOneProps {
  className?: string;
  onSubmit: () => void;
}

export const StepOne = (props: StepOneProps) => {
  const registerFirstStore = useRegisterFirstStore();

  const { control, formState, handleSubmit, watch, setError } = useForm({ defaultValues: DEFAULT_VALUES });
  const cardNumber = watch('cardNumber');
  const mobileNumber = watch('mobileNumber');

  const { t } = useTranslation('register');

  const submitHandler = async (data: IForm) => {
    const result = await registerFirstStore.registerFirst({
      CardNumber: data.cardNumber,
      MobileAreaCode: data.mobileNumber.code,
      MobilePhoneNumber: data.mobileNumber.number,
      MobileNumber: `${data.mobileNumber.code}${data.mobileNumber.number}`,
    });
    if (result.error) {
      setError('mobileNumber', { message: result.error });
      return;
    }
    props.onSubmit();
  };

  return (
    <form className={clsx('mt-8', props.className)} noValidate autoComplete='off' onSubmit={handleSubmit(submitHandler)}>
      <Controller
        name='cardNumber'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          minLength: { value: 4, message: t('stepOne.errors.form.last4Digits') },
          maxLength: { value: 4, message: t('stepOne.errors.form.last4Digits') },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            captionClassName='!text-lg !font-light'
            caption={t('stepOne.placeholders.enterLast4Digits')}
            name='cardNumber'
            minLength={4}
            maxLength={4}
            inputPattern='\d'
            error={error?.message}
          />
        )}
      />
      <Controller
        name='mobileNumber'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          validate: (value) => {
            if (!value.code) return t('errors.form.required', { ns: 'common' });
            if (!value.number) return t('errors.form.required', { ns: 'common' });
            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <PhoneInput {...field} caption={t('stepOne.placeholders.mobileNumber')} name='mobileNumber' error={error?.message} />
        )}
      />
      <Button
        className='text-base mt-15 h-[50px]'
        fullWidth
        caption={t('stepOne.buttons.sendCode')}
        type='submit'
        disabled={!cardNumber || !mobileNumber || !mobileNumber.code || !mobileNumber.number || formState.isSubmitting}
      />
    </form>
  );
};
